import { captureException, SeverityLevel } from '@sentry/react';
import { AxiosError } from 'axios';

import CancellableAPI from '../../api/CancellableAPI';
import { CognitoAuthError, CognitoErrorCodes } from '../../types/types';
import { checkIsNetworkError, checkIsSpecificCode, getErrorResponse } from './APIErrorsHandlers';

const SENSITIVE_BODY_KEYS = ['password', 'newPassword', 'oldPassword', 'currentPassword'];

export const redactSensitiveData = (data: unknown): unknown => {
    try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        if (parsed && typeof parsed === 'object') {
            const redacted = { ...(parsed as Record<string, unknown>) };
            SENSITIVE_BODY_KEYS.forEach((key) => {
                if (key in redacted) redacted[key] = '[REDACTED]';
            });
            return typeof data === 'string' ? JSON.stringify(redacted) : redacted;
        }
    } catch {
        // not JSON; fall through
    }
    return data;
};

const redactAxiosConfigData = (error: Error): Error => {
    const axiosError = error as AxiosError;
    if (!axiosError.config?.data) {
        return error;
    }
    axiosError.config = {
        ...axiosError.config,
        data: redactSensitiveData(axiosError.config.data),
    };
    return error;
};

// ---------------------------------------------------------------------------
// Error classification
// ---------------------------------------------------------------------------

export type SentryErrorKind = 'api' | 'app' | 'cognito' | 'non-error';

export interface SentryErrorContext {
    context: string;
    kind: SentryErrorKind;
    httpStatus?: string;
    cognitoErrorCode?: string;
}

/**
 * Non-enumerable property key used to attach Sentry routing metadata directly
 * to an error object so that beforeSend can read it from hint.originalException.
 * Using a symbol avoids collisions and keeps the property hidden from
 * JSON.stringify / Object.keys iteration.
 */
const SENTRY_CTX = Symbol('sentryCtx');

const attachSentryCtx = (error: Error, ctx: SentryErrorContext): void => {
    Object.defineProperty(error, SENTRY_CTX, { value: ctx, enumerable: false, configurable: true });
};

/** Read the attached context from an error captured via captureErrorForSentry. */
export const getSentryCtx = (error: unknown): SentryErrorContext | undefined => (
    (error as Record<symbol, SentryErrorContext> | null)?.[SENTRY_CTX]
);

// ---------------------------------------------------------------------------
// HTTP-status helpers
// ---------------------------------------------------------------------------

const STABLE_NON_ERROR_MESSAGE = 'Non-Error exception captured';

const prepareNonErrorException = (): Error => new Error(STABLE_NON_ERROR_MESSAGE);

export const prepareErrorForSentry = (error: unknown): Error => {
    if (error instanceof Error) {
        return redactAxiosConfigData(error);
    }
    return prepareNonErrorException();
};

const WARNING_STATUSES_LIST: number[] = [
    401,
    403,
    404,
    406,
    409,
    412,
    415,
];

const WARNING_STATUSES_LIST_WITH_400: number[] = [400, ...WARNING_STATUSES_LIST];

export type SentryAdapter = (error: unknown, name: string) => void;

export type HTTPStatusFilterCondition = 'expected4xx' | 'only404' | 'allow400' | undefined;

export interface ErrorCaptureConfig {
    httpStatusFilterCondition?: HTTPStatusFilterCondition;
    severityLevel?: SeverityLevel;
}

const CODES_DICT: Record<HTTPStatusFilterCondition, number | number[]> = {
    expected4xx: WARNING_STATUSES_LIST,
    only404: 404,
    allow400: WARNING_STATUSES_LIST_WITH_400,
};

const getHttpStatusLabel = (error: unknown): string | null => {
    const { statusCode } = getErrorResponse(error);
    if (statusCode) {
        return String(statusCode);
    }
    if (checkIsNetworkError(error)) {
        return 'network';
    }
    const axiosLike = error as AxiosError;
    if (axiosLike?.isAxiosError || axiosLike?.config) {
        return 'unknown';
    }
    return null;
};

const classifyError = (error: unknown): { kind: SentryErrorKind; httpStatus?: string } => {
    const httpStatus = getHttpStatusLabel(error);
    if (httpStatus) {
        return { kind: 'api', httpStatus };
    }
    if (error instanceof Error) {
        return { kind: 'app' };
    }
    return { kind: 'non-error' };
};

const getSeverityLevel = (error: unknown, httpStatusFilterCondition: HTTPStatusFilterCondition): SeverityLevel => {
    let level: SeverityLevel = 'error';
    const isWarning: boolean = (
        !!httpStatusFilterCondition
        && checkIsSpecificCode(error, CODES_DICT[httpStatusFilterCondition])
    );
    if (isWarning) {
        level = 'warning';
    }
    return level;
};

// ---------------------------------------------------------------------------
// Public capture helpers
// ---------------------------------------------------------------------------

export const captureErrorForSentry = (error: unknown, name: string, config: ErrorCaptureConfig = {}): void => {
    if (!CancellableAPI.isAnyInstanceCancel(error)) {
        const { httpStatusFilterCondition, severityLevel } = config;
        const errorItem: Error = prepareErrorForSentry(error);
        const { kind, httpStatus } = classifyError(error);
        const level: SeverityLevel = severityLevel || getSeverityLevel(error, httpStatusFilterCondition);

        attachSentryCtx(errorItem, { context: name, kind, httpStatus });

        captureException(errorItem, {
            level,
            ...(error instanceof Error ? {} : { extra: { originalPayload: error } }),
        });
    }
};

export const captureWarningForSentry = (error: unknown, name: string): void => {
    captureErrorForSentry(error, name, { severityLevel: 'warning' });
};

export const captureUnexpectedNetworkError = (error: unknown, name: string): void => {
    captureErrorForSentry(error, name, { httpStatusFilterCondition: 'expected4xx' });
};

export const captureServerError = (error: unknown, name: string): void => {
    captureErrorForSentry(error, name, { httpStatusFilterCondition: 'allow400' });
};

export const captureNon404Error = (error: unknown, name: string): void => {
    captureErrorForSentry(error, name, { httpStatusFilterCondition: 'only404' });
};

const errorToJSONString = (error: unknown): string => JSON.stringify(error, Object.getOwnPropertyNames(error));

export const captureOnce = (adapter: SentryAdapter): SentryAdapter => {
    const capturedErrorsSet: Set<string> = new Set<string>();

    return (error: unknown, name: string): void => {
        const errorJSONStr: string = errorToJSONString(error);
        if (!capturedErrorsSet.has(errorJSONStr)) {
            capturedErrorsSet.add(errorJSONStr);
            adapter(error, name);
        }
    };
};

// ---------------------------------------------------------------------------
// Cognito errors
// ---------------------------------------------------------------------------

type CognitoErrorKeys = keyof CognitoAuthError;

const COGNITO_ERROR_KEYS: CognitoErrorKeys[] = ['name', 'message', 'code'];

const checkIsCognitoError = (error: unknown): boolean => (
    !(error instanceof Error)
    && COGNITO_ERROR_KEYS.every((key) => Object.prototype.hasOwnProperty.call(error, key))
);

const COGNITO_WARNING_LEVEL_CODES_SET: ReadonlySet<CognitoErrorCodes> = new Set<CognitoErrorCodes>([
    CognitoErrorCodes.CodeMismatchException,
    CognitoErrorCodes.NotAuthorizedException,
    CognitoErrorCodes.UsernameExistsException,
    CognitoErrorCodes.NotAuthorizedExceptionSignIn,
    CognitoErrorCodes.UserNotConfirmedException,
    CognitoErrorCodes.UserNotFoundException,
    CognitoErrorCodes.UserNotFoundExceptionSignIn,
    CognitoErrorCodes.NetworkError,
]);

const getCognitoErrorSeverityLevel = (code: CognitoErrorCodes | string): SeverityLevel => (
    COGNITO_WARNING_LEVEL_CODES_SET.has(code as CognitoErrorCodes)
        ? 'warning'
        : 'error'
);

export const captureCognitoError = (error: unknown, name: string): void => {
    if (checkIsCognitoError(error)) {
        const cognitoError = error as CognitoAuthError;
        const level: SeverityLevel = getCognitoErrorSeverityLevel(cognitoError.code);
        const errorItem = new Error(cognitoError.message);
        attachSentryCtx(errorItem, {
            context: name,
            kind: 'cognito',
            cognitoErrorCode: cognitoError.code,
        });
        captureException(errorItem, {
            level,
            extra: { originalPayload: error },
        });
    } else if (typeof error === 'string') {
        captureWarningForSentry(error, name);
    } else {
        captureErrorForSentry(error, name);
    }
};
